import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Building2, MapPin, DollarSign, Loader2, Target, Sparkles, Users, Shield, 
  CheckCircle, TrendingUp, ExternalLink, Mail, Linkedin, Copy, Globe,
  Briefcase, AlertTriangle, ChevronRight, Calendar, Zap, XCircle, Clock,
  Link, Search, Save, Info, Lock, Lightbulb, Kanban, Plus, RefreshCw, Crown, Users2
} from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import PaywallModal from "@/components/PaywallModal";
import ContributionModal from "@/components/ContributionModal";
import { FUNDRAISING_TIMEFRAMES } from "./profileConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getScoreColor = (score) => {
  if (score >= 80) return "text-green-600 bg-green-50 border-green-200";
  if (score >= 60) return "text-teal-600 bg-teal-50 border-teal-200";
  if (score >= 40) return "text-amber-600 bg-amber-50 border-amber-200";
  return "text-slate-600 bg-slate-50 border-slate-200";
};

const getScoreLabel = (score) => {
  if (score >= 80) return "Excellent Fit";
  if (score >= 60) return "Strong Fit";
  if (score >= 40) return "Good Fit";
  return "Potential Fit";
};

function MatchedInvestorCard({ investor, onClick }) {
  return (
    <Card 
      className="hover:shadow-lg cursor-pointer border-2 transition-all hover:border-teal-200" 
      onClick={onClick}
      data-testid={`investor-card-${investor.firm_id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">{investor.name?.charAt(0)}</span>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 text-lg">{investor.name}</h3>
              {investor.location && (
                <div className="flex items-center gap-1 text-slate-500 text-sm">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{investor.location}</span>
                </div>
              )}
            </div>
          </div>
          <div className={`px-3 py-2 rounded-lg border ${getScoreColor(investor.match_score)}`}>
            <div className="text-2xl font-bold">{investor.match_score}%</div>
            <div className="text-xs">{getScoreLabel(investor.match_score)}</div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-1.5 mb-3">
          {investor.match_reasons?.slice(0, 3).map((reason, i) => (
            <Badge key={i} variant="secondary" className="text-xs">
              <CheckCircle className="w-3 h-3 mr-1" />
              {reason}
            </Badge>
          ))}
          {investor.eis_seis_only && (
            <Badge className="text-xs bg-amber-100 text-amber-700">EIS/SEIS Only</Badge>
          )}
        </div>
        
        {investor.investment_thesis && (
          <p className="text-slate-600 text-sm line-clamp-2 mb-3">
            {investor.investment_thesis}
          </p>
        )}
        
        {/* Similar Portfolio Companies */}
        {investor.similar_portfolio_companies?.length > 0 && (
          <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
            <div className="text-xs text-blue-700 font-medium mb-1">Similar companies in portfolio:</div>
            <div className="flex flex-wrap gap-1">
              {investor.similar_portfolio_companies.map((company, i) => (
                <Badge key={i} variant="outline" className="text-xs bg-white">
                  {company.name}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex flex-wrap gap-3 text-sm text-slate-500">
          {investor.stage_focus?.length > 0 && (
            <div className="flex items-center gap-1">
              <Target className="w-3.5 h-3.5" />
              {investor.stage_focus.slice(0, 2).join(", ")}
            </div>
          )}
          {(investor.min_ticket_size || investor.max_ticket_size) && (
            <div className="flex items-center gap-1">
              <DollarSign className="w-3.5 h-3.5" />
              {investor.min_ticket_size} - {investor.max_ticket_size}
            </div>
          )}
          {investor.leads_rounds && (
            <Badge className="text-xs bg-green-100 text-green-700">Leads Rounds</Badge>
          )}
        </div>
        
        <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
          <Button variant="ghost" size="sm" className="text-teal-600 gap-1">
            View Details <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function InvestorDetailDialog({ investor, open, onClose, companyProfile, onRefreshAccessPath, onAddToPipeline, addingToPipeline }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [draftingMessage, setDraftingMessage] = useState(false);
  const [draftedMessage, setDraftedMessage] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [proposedDateTime, setProposedDateTime] = useState("");
  const [showConnectionGraph, setShowConnectionGraph] = useState(false);
  const [connectionInputs, setConnectionInputs] = useState(["", "", "", "", ""]);
  const [savingConnections, setSavingConnections] = useState(false);
  
  // Access Path state
  const [accessPathData, setAccessPathData] = useState(null);
  const [loadingAccessPath, setLoadingAccessPath] = useState(false);
  const [generatingCard, setGeneratingCard] = useState(false);
  const [operonCard, setOperonCard] = useState(null);
  
  // Fetch access path when dialog opens and tab is "contact"
  useEffect(() => {
    if (open && investor?.firm_id && activeTab === "contact") {
      fetchAccessPath();
    }
  }, [open, investor?.firm_id, activeTab]);
  
  const fetchAccessPath = async () => {
    if (!investor?.firm_id) return;
    setLoadingAccessPath(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/investors/${investor.firm_id}/access-path`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setAccessPathData(data);
      }
    } catch (e) {
      console.error("Failed to fetch access path:", e);
    }
    setLoadingAccessPath(false);
  };
  
  const handleKnowContact = async (contact) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/network/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: contact.name,
          company: contact.company,
          role: contact.role,
          linkedin_url: contact.linkedin_url,
          source: "learned"
        })
      });
      if (res.ok) {
        toast.success(`Added ${contact.name} to your network!`);
        // Refresh access path to reflect new connection
        fetchAccessPath();
        if (onRefreshAccessPath) onRefreshAccessPath();
      }
    } catch (e) {
      toast.error("Failed to add contact");
    }
  };
  
  const handleGenerateCard = async () => {
    setGeneratingCard(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/operon-card/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ investor_firm_id: investor.firm_id })
      });
      if (res.ok) {
        const data = await res.json();
        setOperonCard(data.card);
        toast.success("Operon Card generated!");
      }
    } catch (e) {
      toast.error("Failed to generate card");
    }
    setGeneratingCard(false);
  };
  
  const copyCardLink = () => {
    const link = `${window.location.origin}/card/${operonCard.card_id}`;
    navigator.clipboard.writeText(link);
    toast.success("Card link copied!");
  };
  
  if (!investor) return null;
  
  const companyName = companyProfile?.company_name || "our company";
  const companyIndications = companyProfile?.indications || [];
  const companyModality = companyProfile?.modality || "";
  const companyDescription = companyProfile?.description || "";
  const companyOneLiner = companyProfile?.one_liner || "";
  
  const portfolioExited = investor.portfolio_exited || {};
  const portfolioCurrent = investor.portfolio_current || {};
  const contacts = investor.contacts || [];
  
  // Analyze for conflicts in CURRENT portfolio
  const conflicts = [];
  const opportunities = [];
  
  Object.entries(portfolioCurrent).forEach(([company, details]) => {
    const hasIndicationOverlap = companyIndications.some(ind => 
      details.indication?.toLowerCase().includes(ind.toLowerCase()) ||
      ind.toLowerCase().includes(details.indication?.toLowerCase() || "")
    );
    const hasModalityOverlap = companyModality && details.modality && (
      details.modality.toLowerCase().includes(companyModality.toLowerCase()) ||
      companyModality.toLowerCase().includes(details.modality.toLowerCase())
    );
    
    if (hasIndicationOverlap && hasModalityOverlap) {
      conflicts.push({ company, ...details, reason: "Same indication + modality" });
    } else if (hasIndicationOverlap && !hasModalityOverlap) {
      opportunities.push({ company, ...details, reason: `Indication overlap, different modality (${details.modality})` });
    }
  });
  
  // Find modality gaps (what they DON'T have in current portfolio)
  const currentModalities = new Set(Object.values(portfolioCurrent).map(d => d.modality?.toLowerCase()).filter(Boolean));
  const modalityGap = companyModality && !([...currentModalities].some(m => 
    m.includes(companyModality.toLowerCase()) || companyModality.toLowerCase().includes(m)
  ));
  
  // Generate trust network - Portfolio CEOs, co-investors, etc.
  const trustNetwork = [];
  // Add portfolio company leaders
  Object.keys(portfolioCurrent).slice(0, 3).forEach((company, i) => {
    trustNetwork.push({
      name: `${company} Leadership`,
      role: "Portfolio Company CEO",
      company: company,
      type: "portfolio_ceo"
    });
  });
  // Add contacts as potential connectors
  contacts.slice(0, 2).forEach(c => {
    trustNetwork.push({
      name: c.name,
      role: c.role || "Partner",
      company: investor.name,
      type: "direct_contact"
    });
  });
  // Pad to 6 if needed
  while (trustNetwork.length < 6) {
    trustNetwork.push({
      name: "Co-investor Network",
      role: "Frequent Co-investor",
      company: "Shared Deals",
      type: "coinvestor"
    });
  }

  const handleDraftMessage = async (contact) => {
    setSelectedContact(contact);
    setDraftingMessage(true);
    
    const firstName = contact.name.split(" ")[0];
    
    // Find a strategic angle
    let strategicHook = "";
    
    if (opportunities.length > 0) {
      const opp = opportunities[0];
      strategicHook = `I noticed ${investor.name} has conviction in ${opp.indication} through ${opp.company}. We're approaching the same biology from a completely different angle - ${companyModality || "a novel modality"} - which could be complementary rather than competitive.`;
    } else if (modalityGap && companyIndications.length > 0) {
      strategicHook = `Looking at ${investor.name}'s portfolio, I see strong ${companyIndications[0]} coverage but noticed a gap in ${companyModality}. We think this modality unlocks targets that have been undruggable with conventional approaches.`;
    } else {
      const exitedNames = Object.keys(portfolioExited).slice(0, 2);
      if (exitedNames.length > 0) {
        strategicHook = `The pattern I see in ${investor.name}'s best outcomes - backing differentiated science early - is exactly where we sit. We've identified something others have missed.`;
      } else {
        strategicHook = `We're building in a space where the conventional approaches have hit a wall. Our data suggests a path forward that hasn't been tried.`;
      }
    }
    
    let companyHook = "";
    if (companyOneLiner) {
      companyHook = companyOneLiner;
    } else if (companyDescription) {
      const exciting = companyDescription.split(".")[0];
      companyHook = exciting.length < 150 ? exciting : companyDescription.slice(0, 120) + "...";
    } else {
      companyHook = `We're working on something that could redefine how ${companyIndications[0] || "this indication"} is treated.`;
    }
    
    const message = `${firstName},

${strategicHook}

${companyName}: ${companyHook}

We have early data that's surprised us. Worth 20 mins${proposedDateTime ? ` ${proposedDateTime}` : ""}?

Best,
[Your name]`;
    
    setDraftedMessage(message);
    setDraftingMessage(false);
  };
  
  const copyMessage = () => {
    navigator.clipboard.writeText(draftedMessage);
    toast.success("Message copied to clipboard");
  };

  const handleSaveConnections = async () => {
    const validConnections = connectionInputs.filter(c => c.trim());
    if (validConnections.length === 0) {
      toast.error("Please enter at least one connection");
      return;
    }
    setSavingConnections(true);
    try {
      await fetch(`${BACKEND_URL}/api/connections/add`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          investor_id: investor.firm_id,
          connections: validConnections.map(name => ({ name: name.trim() }))
        })
      });
      toast.success("Connections saved! This helps us build better intro paths.");
      setShowConnectionGraph(false);
      setConnectionInputs(["", "", "", "", ""]);
    } catch (e) {
      toast.error("Failed to save connections");
    } finally {
      setSavingConnections(false);
    }
  };
  
  const renderPortfolioTab = (portfolio, isExited) => {
    if (Object.keys(portfolio).length === 0) {
      return (
        <div className="text-center py-6 text-slate-500">
          No {isExited ? "exits" : "current investments"} on record
        </div>
      );
    }
    
    return (
      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {Object.entries(portfolio).map(([company, details], i) => {
          const isConflict = !isExited && conflicts.some(c => c.company === company);
          const isOpportunity = !isExited && opportunities.some(o => o.company === company);
          
          return (
            <div 
              key={i} 
              className={`p-3 rounded-lg border ${
                isConflict ? "bg-red-50 border-red-200" : 
                isOpportunity ? "bg-amber-50 border-amber-200" : 
                "bg-slate-50 border-slate-200"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">{company}</span>
                  {isConflict && (
                    <Badge variant="destructive" className="text-xs">
                      <XCircle className="w-3 h-3 mr-1" />
                      Conflict
                    </Badge>
                  )}
                  {isOpportunity && (
                    <Badge className="text-xs bg-amber-100 text-amber-700">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Complementary
                    </Badge>
                  )}
                </div>
                {isExited ? (
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Exited</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">{details.stage}</Badge>
                )}
              </div>
              <div className="text-sm text-slate-600">
                <span className="text-slate-500">Indication:</span> {details.indication} • 
                <span className="text-slate-500 ml-2">Modality:</span> {details.modality}
              </div>
              {isExited && details.outcome && (
                <div className="text-xs text-green-600 mt-1">
                  <Zap className="w-3 h-3 inline mr-1" />
                  {details.outcome}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-600 to-emerald-700 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl">{investor.name}</DialogTitle>
              <DialogDescription className="flex items-center gap-2">
                {investor.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {investor.location}
                  </span>
                )}
                {investor.website && (
                  <a href={investor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal-600 hover:underline">
                    <Globe className="w-3.5 h-3.5" />
                    Website
                  </a>
                )}
              </DialogDescription>
            </div>
            <Badge className={`${getScoreColor(investor.match_score)} px-4 py-2 text-lg`}>
              {investor.match_score}%
            </Badge>
          </div>
          {/* Add to CRM button */}
          <div className="mt-3">
            <Button 
              onClick={() => onAddToPipeline && onAddToPipeline(investor)}
              disabled={addingToPipeline}
              className="w-full gap-2"
              data-testid="add-to-crm-btn"
            >
              {addingToPipeline ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Kanban className="w-4 h-4" />
              )}
              Add to CRM
            </Button>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="contact">How to Connect</TabsTrigger>
            <TabsTrigger value="outreach">Draft Message</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4 pt-4">
            {/* Score Breakdown */}
            <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
              <h4 className="font-semibold text-teal-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Score Breakdown
              </h4>
              
              {/* Hard Gates Section - Only thesis, stage, geography */}
              {investor.score_breakdown?.gates && (
                <div className="mb-4">
                  <p className="text-xs text-teal-700 font-medium mb-2">HARD GATES (must all pass)</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(investor.score_breakdown.gates)
                      .filter(([gateName]) => gateName !== "portfolio_conflict") // Exclude portfolio_conflict
                      .map(([gateName, gate]) => (
                      <div 
                        key={gateName} 
                        className={`p-2 rounded border text-xs ${
                          gate.passed 
                            ? "bg-green-50 border-green-200" 
                            : "bg-red-50 border-red-200"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium capitalize">{gateName.replace(/_/g, " ")}</span>
                          {gate.passed ? (
                            <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 text-red-600" />
                          )}
                        </div>
                        <p className={`mt-1 ${gate.passed ? "text-green-700" : "text-red-700"}`}>
                          {gate.reason}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Soft Scoring Variables Section (only if gates pass) */}
              {investor.score_breakdown?.all_gates_passed && investor.score_breakdown?.additive_scores && (
                <div className="pt-3 border-t border-teal-200">
                  <p className="text-xs text-teal-700 font-medium mb-3">SOFT SCORING VARIABLES</p>
                  <div className="space-y-3">
                    {/* Portfolio Compatibility Box */}
                    {(() => {
                      const score = investor.score_breakdown.additive_scores.portfolio_compatibility;
                      const scoreOutOf10 = Math.round(score / 10);
                      const hasConflict = investor.has_conflict && investor.conflict_details?.length > 0;
                      const boxColor = score >= 70 ? "bg-green-50 border-green-200" : score >= 40 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";
                      const textColor = score >= 70 ? "text-green-800" : score >= 40 ? "text-amber-800" : "text-red-800";
                      const descColor = score >= 70 ? "text-green-700" : score >= 40 ? "text-amber-700" : "text-red-700";
                      const scoreColor = score >= 70 ? "text-green-600" : score >= 40 ? "text-amber-600" : "text-red-600";
                      
                      return (
                        <div className={`p-3 rounded-lg border ${boxColor}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-medium ${textColor}`}>Portfolio Compatibility</span>
                            <span className={`text-sm font-bold ${scoreColor}`}>{scoreOutOf10}/10</span>
                          </div>
                          <p className={`text-xs ${descColor}`}>
                            {hasConflict 
                              ? `Potential conflict detected with ${investor.conflict_details[0]?.company} (${investor.conflict_details[0]?.indication}, ${investor.conflict_details[0]?.modality}). This may impact portfolio fit but doesn't prevent outreach.`
                              : "No portfolio conflicts detected. Your company fits well with their existing portfolio based on archetypes and indications they've invested in."
                            }
                          </p>
                        </div>
                      );
                    })()}
                    
                    {/* Deployment Likelihood Box */}
                    {(() => {
                      const score = investor.score_breakdown.additive_scores.deployment_likelihood;
                      const scoreOutOf10 = Math.round(score / 10);
                      const boxColor = score >= 70 ? "bg-green-50 border-green-200" : score >= 40 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";
                      const textColor = score >= 70 ? "text-green-800" : score >= 40 ? "text-amber-800" : "text-red-800";
                      const descColor = score >= 70 ? "text-green-700" : score >= 40 ? "text-amber-700" : "text-red-700";
                      const scoreColor = score >= 70 ? "text-green-600" : score >= 40 ? "text-amber-600" : "text-red-600";
                      
                      return (
                        <div className={`p-3 rounded-lg border ${boxColor}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-medium ${textColor}`}>Deployment Likelihood</span>
                            <span className={`text-sm font-bold ${scoreColor}`}>{scoreOutOf10}/10</span>
                          </div>
                          <p className={`text-xs ${descColor}`}>
                            {score >= 70 
                              ? "High activity investor. Made multiple investments in the last 6-12 months, actively deploying capital."
                              : score >= 40
                              ? "Moderate activity. Some recent investments suggest they may be selectively deploying."
                              : "Low recent activity. Limited investments in the last 12 months, may be less actively deploying."
                            }
                          </p>
                        </div>
                      );
                    })()}
                    
                    {/* Leading Likelihood Box */}
                    {(() => {
                      const score = investor.score_breakdown.additive_scores.leading_likelihood || 0;
                      const scoreOutOf10 = Math.round(score / 10);
                      const boxColor = score >= 70 ? "bg-green-50 border-green-200" : score >= 40 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";
                      const textColor = score >= 70 ? "text-green-800" : score >= 40 ? "text-amber-800" : "text-red-800";
                      const descColor = score >= 70 ? "text-green-700" : score >= 40 ? "text-amber-700" : "text-red-700";
                      const scoreColor = score >= 70 ? "text-green-600" : score >= 40 ? "text-amber-600" : "text-red-600";
                      
                      return (
                        <div className={`p-3 rounded-lg border ${boxColor}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-medium ${textColor}`}>Leading Likelihood</span>
                            <span className={`text-sm font-bold ${scoreColor}`}>{scoreOutOf10}/10</span>
                          </div>
                          <p className={`text-xs ${descColor}`}>
                            {score >= 70 
                              ? "Frequently leads rounds in your space. Has led multiple rounds in the last 24 months."
                              : score >= 40
                              ? "Occasionally leads rounds. May lead or co-lead depending on the opportunity."
                              : "Typically follows rather than leads. More likely to join rounds led by others."
                            }
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
              
              {/* Rationale Summary */}
              <p className="text-teal-800 text-sm mt-3 pt-3 border-t border-teal-200">{investor.match_rationale}</p>
            </div>

            {/* Investment Thesis */}
            {investor.investment_thesis && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Investment Thesis
                </h4>
                <p className="text-slate-600 text-sm">{investor.investment_thesis}</p>
              </div>
            )}

            {/* Key Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              {investor.stage_focus?.length > 0 && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-sm text-slate-500 mb-2">Stage Focus</div>
                  <div className="flex flex-wrap gap-1">
                    {investor.stage_focus.map((s, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {investor.focus_areas?.length > 0 && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-sm text-slate-500 mb-2">Therapeutic Focus</div>
                  <div className="flex flex-wrap gap-1">
                    {investor.focus_areas.slice(0, 5).map((a, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{a}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Ticket Size */}
            {(investor.min_ticket_size || investor.max_ticket_size) && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <DollarSign className="w-4 h-4" />
                <span>Typical check: {investor.min_ticket_size} - {investor.max_ticket_size}</span>
              </div>
            )}
          </TabsContent>

          {/* PORTFOLIO TAB */}
          <TabsContent value="portfolio" className="space-y-4 pt-4">
            <Tabs defaultValue="current" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="current" className="text-sm">
                  <Briefcase className="w-3.5 h-3.5 mr-1.5" />
                  Current ({Object.keys(portfolioCurrent).length})
                </TabsTrigger>
                <TabsTrigger value="exited" className="text-sm">
                  <Zap className="w-3.5 h-3.5 mr-1.5" />
                  Exits ({Object.keys(portfolioExited).length})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="current" className="mt-3">
                {renderPortfolioTab(portfolioCurrent, false)}
              </TabsContent>
              <TabsContent value="exited" className="mt-3">
                {renderPortfolioTab(portfolioExited, true)}
              </TabsContent>
            </Tabs>

            {/* Opportunities */}
            {opportunities.length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Synergy Opportunities
                </h4>
                <p className="text-amber-800 text-sm">
                  Reference their investment in {opportunities[0].company} - you offer a complementary approach ({opportunities[0].reason}).
                </p>
              </div>
            )}
          </TabsContent>

          {/* HOW TO CONNECT TAB - NEW ACCESS PATH UI */}
          <TabsContent value="contact" className="space-y-4 pt-4">
            {loadingAccessPath ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                <span className="ml-2 text-slate-600">Loading access paths...</span>
              </div>
            ) : (
              <>
                {/* Warm Intros Section */}
                {accessPathData?.warm_intros?.length > 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Warm Intro Available!
                    </h4>
                    <div className="space-y-2">
                      {accessPathData.warm_intros.map((intro, i) => (
                        <div key={i} className="p-3 bg-white rounded-lg border border-green-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-slate-900">{intro.name}</div>
                              <div className="text-sm text-slate-600">{intro.role} at {intro.company}</div>
                              <div className="text-xs text-green-700 mt-1">{intro.comment}</div>
                            </div>
                            {intro.linkedin_url && (
                              <Button variant="ghost" size="sm" onClick={() => window.open(intro.linkedin_url, '_blank')}>
                                <Linkedin className="w-4 h-4 text-blue-600" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No User Investors Warning */}
                {!accessPathData?.has_user_investors && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Add your investors to unlock intro paths
                    </h4>
                    <p className="text-sm text-amber-800 mb-3">
                      Tell us which investors you've worked with before. We'll find connections between your network and {investor.name}'s trusted contacts.
                    </p>
                    <Button 
                      variant="outline" 
                      className="border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={() => window.location.href = "/profile"}
                    >
                      Add My Investors
                    </Button>
                  </div>
                )}

                {/* Trusted Network Discovery */}
                {accessPathData?.discovery_contacts?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {investor.name}'s Trusted Network
                    </h4>
                    <p className="text-sm text-slate-600 mb-4">
                      Do you know any of these people? Click to add them to your network.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {accessPathData.discovery_contacts.map((contact, i) => (
                        <div 
                          key={i} 
                          className="p-3 bg-slate-50 border border-slate-200 rounded-lg hover:border-teal-300 hover:bg-teal-50 cursor-pointer transition-all group"
                          onClick={() => handleKnowContact(contact)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-sm text-slate-900 flex items-center gap-2">
                                {contact.name}
                                {contact.linkedin_url && (
                                  <Linkedin className="w-3.5 h-3.5 text-blue-500" onClick={(e) => { e.stopPropagation(); window.open(contact.linkedin_url, '_blank'); }} />
                                )}
                              </div>
                              {contact.company && (
                                <div className="text-xs text-slate-600">{contact.role ? `${contact.role}, ` : ''}{contact.company}</div>
                              )}
                              <div className="text-xs text-teal-600 mt-1">{contact.comment}</div>
                            </div>
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <Badge variant="outline" className="text-xs bg-teal-100 text-teal-700 border-teal-300">
                                I know them
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {accessPathData.total_network_size > 7 && (
                      <p className="text-xs text-slate-500 mt-3 text-center">
                        + {accessPathData.total_network_size - 7} more in their network
                      </p>
                    )}
                  </div>
                )}

                {/* Operon Card Section */}
                <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <h4 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Share Your Operon Card
                  </h4>
                  <p className="text-sm text-indigo-800 mb-3">
                    Generate a shareable card with your company highlights and a personalized pitch for {investor.name}.
                  </p>
                  
                  {!operonCard ? (
                    <Button 
                      onClick={handleGenerateCard} 
                      disabled={generatingCard}
                      variant="outline" 
                      className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                    >
                      {generatingCard ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Operon Card
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-4 bg-white rounded-lg border border-indigo-200">
                        <div className="font-semibold text-slate-900">{operonCard.company_name}</div>
                        <div className="text-sm text-slate-600 mt-1">{operonCard.one_liner}</div>
                        <div className="text-sm text-indigo-700 mt-2 italic">"{operonCard.pitch_blurb}"</div>
                        <div className="flex gap-2 mt-2">
                          {operonCard.indications?.map((ind, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{ind}</Badge>
                          ))}
                          {operonCard.modality && <Badge variant="outline" className="text-xs">{operonCard.modality}</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={copyCardLink} variant="outline" className="flex-1">
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Link
                        </Button>
                        <Button onClick={() => window.open(`/card/${operonCard.card_id}`, '_blank')} className="flex-1">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Preview Card
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Direct Contacts */}
                {contacts.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Direct Contacts
                    </h4>
                    <div className="space-y-2">
                      {contacts.map((contact, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                          <div>
                            <div className="font-medium text-slate-900">{contact.name}</div>
                            <div className="text-sm text-slate-500">{contact.role}</div>
                          </div>
                          <div className="flex gap-2">
                            {contact.email && (
                              <Button variant="ghost" size="sm" onClick={() => window.open(`mailto:${contact.email}`)}>
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                            {contact.linkedin && (
                              <Button variant="ghost" size="sm" onClick={() => window.open(contact.linkedin, '_blank')}>
                                <Linkedin className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Events */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Upcoming Events They're Attending
                  </h4>
                  {investor.events_attending && investor.events_attending.length > 0 ? (
                    <div className="space-y-2">
                      {investor.events_attending.map((eventName, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 bg-white rounded border border-blue-100">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Calendar className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-slate-900 text-sm">{eventName}</div>
                            <div className="text-xs text-blue-600">Great opportunity to connect in person</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-blue-700">No confirmed event attendance found.</p>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* DRAFT MESSAGE TAB */}
          <TabsContent value="outreach" className="space-y-4 pt-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-3">Select a Contact</h4>
              
              <div className="mb-4">
                <Label htmlFor="meeting-time" className="text-xs text-slate-500">
                  Proposed time (optional)
                </Label>
                <Input
                  id="meeting-time"
                  type="text"
                  placeholder="e.g., Tuesday at 2pm?"
                  value={proposedDateTime}
                  onChange={(e) => setProposedDateTime(e.target.value)}
                  className="mt-1 h-8 text-sm"
                />
              </div>
              
              {contacts.length > 0 ? (
                <div className="space-y-2">
                  {contacts.map((contact, i) => (
                    <div 
                      key={i} 
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedContact?.name === contact.name 
                          ? "border-teal-500 bg-teal-50" 
                          : "border-slate-200 hover:border-teal-300"
                      }`}
                      onClick={() => handleDraftMessage(contact)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-slate-900">{contact.name}</div>
                          <div className="text-sm text-slate-500">{contact.role}</div>
                        </div>
                        <Button 
                          size="sm" 
                          variant={selectedContact?.name === contact.name ? "default" : "outline"}
                          disabled={draftingMessage}
                        >
                          {draftingMessage && selectedContact?.name === contact.name ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>Draft Message</>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No contacts available. Use "How to Connect" tab to find introduction paths.</p>
              )}
            </div>

            {/* Drafted Message */}
            {draftedMessage && (
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-teal-900">Your Message</h4>
                  <Button size="sm" variant="outline" onClick={copyMessage} className="gap-1">
                    <Copy className="w-4 h-4" />
                    Copy
                  </Button>
                </div>
                <pre className="text-sm text-teal-800 whitespace-pre-wrap font-sans bg-white p-3 rounded border border-teal-100">
                  {draftedMessage}
                </pre>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
function PrepareDialog({ open, onClose, data, loading }) {
  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-xl">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 animate-spin text-teal-500 mb-4" />
            <p className="text-slate-600">Analyzing your investor readiness...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (!data) return null;

  const getScoreColorClass = (score) => {
    if (score >= 70) return "text-green-600";
    if (score >= 50) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 70) return "bg-green-50 border-green-200";
    if (score >= 50) return "bg-amber-50 border-amber-200";
    return "bg-red-50 border-red-200";
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" />
            Investor Readiness Assessment
          </DialogTitle>
          <DialogDescription>
            How ready are you to engage with investors?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Overall Readiness */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Overall Readiness</h4>
              <span className={`text-2xl font-bold ${getScoreColorClass(data.overall_readiness)}`}>
                {data.overall_readiness}%
              </span>
            </div>
            <Progress value={data.overall_readiness} className="h-2" />
            <p className="text-sm text-slate-600 mt-2">{data.readiness_summary}</p>
          </div>

          {/* Funding Stage Likelihood */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-teal-500" />
              Funding Stage Fit
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div className={`p-4 rounded-lg border ${getScoreBgColor(data.seed_score)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Seed</span>
                  <Target className="w-4 h-4 text-slate-400" />
                </div>
                <div className={`text-2xl font-bold ${getScoreColorClass(data.seed_score)}`}>
                  {data.seed_score}%
                </div>
                <p className="text-xs text-slate-500 mt-1">Early validation</p>
              </div>
              <div className={`p-4 rounded-lg border ${getScoreBgColor(data.series_a_score)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Series A</span>
                  <TrendingUp className="w-4 h-4 text-slate-400" />
                </div>
                <div className={`text-2xl font-bold ${getScoreColorClass(data.series_a_score)}`}>
                  {data.series_a_score}%
                </div>
                <p className="text-xs text-slate-500 mt-1">Growth stage</p>
              </div>
              <div className={`p-4 rounded-lg border ${getScoreBgColor(data.series_b_score)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Series B+</span>
                  <DollarSign className="w-4 h-4 text-slate-400" />
                </div>
                <div className={`text-2xl font-bold ${getScoreColorClass(data.series_b_score)}`}>
                  {data.series_b_score}%
                </div>
                <p className="text-xs text-slate-500 mt-1">Scale-up</p>
              </div>
            </div>
          </div>

          {/* Confidence Breakdown */}
          {data.confidence_breakdown?.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Confidence Breakdown
              </h4>
              <div className="space-y-2">
                {data.confidence_breakdown.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-sm text-slate-600 w-36">{item.name}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${item.score >= 70 ? 'bg-green-500' : item.score >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                        style={{ width: `${item.score}%` }}
                      />
                    </div>
                    <span className={`text-sm font-medium w-12 text-right ${getScoreColorClass(item.score)}`}>
                      {item.score}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Questions to Prepare For */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Questions Investors Will Ask
            </h4>
            <div className="space-y-3">
              {data.questions?.map((q, i) => (
                <div key={i} className="p-4 border rounded-lg bg-white">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{q.question}</p>
                      <p className="text-sm text-slate-600 mt-1">{q.why_important}</p>
                      {q.suggestion && (
                        <div className="mt-2 p-2 bg-teal-50 rounded text-sm text-teal-800">
                          <span className="font-medium">Tip:</span> {q.suggestion}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Strengths & Gaps */}
          {(data.strengths?.length > 0 || data.gaps?.length > 0) && (
            <div className="grid grid-cols-2 gap-4">
              {data.strengths?.length > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h5 className="font-medium text-green-900 mb-2">Your Strengths</h5>
                  <ul className="space-y-1">
                    {data.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-green-800 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.gaps?.length > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h5 className="font-medium text-amber-900 mb-2">Gaps to Address</h5>
                  <ul className="space-y-1">
                    {data.gaps.map((g, i) => (
                      <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {g}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// New component for analyzing investor by URL
function AnalyzeInvestorDialog({ open, onClose, onAnalysisComplete }) {
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAnalyze = async () => {
    if (!url.trim()) {
      toast.error("Please enter an investor URL");
      return;
    }
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/investors/analyze-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to analyze investor");
      }
    } catch (error) {
      toast.error("Failed to analyze investor");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    if (!analysis?.investor_data) return;
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/investors/save-analyzed`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ investor_data: analysis.investor_data })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.exists) {
          toast.info("Investor already exists in database");
        } else {
          toast.success("Investor saved to database!");
          onAnalysisComplete?.();
        }
      }
    } catch (error) {
      toast.error("Failed to save investor");
    } finally {
      setSaving(false);
    }
  };

  const getScoreColorClass = (score, max) => {
    const pct = (score / max) * 100;
    if (pct >= 70) return "text-green-600 bg-green-100";
    if (pct >= 50) return "text-amber-600 bg-amber-100";
    return "text-red-600 bg-red-100";
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setUrl(""); setAnalysis(null); } onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-teal-600" />
            Analyze Investor by URL
          </DialogTitle>
          <DialogDescription>
            Paste a VC firm's website to analyze fit with your company
          </DialogDescription>
        </DialogHeader>

        {/* URL Input */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="https://vc-firm-website.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="pl-9"
              onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()}
            />
          </div>
          <Button onClick={handleAnalyze} disabled={analyzing || !url.trim()}>
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
            Analyze
          </Button>
        </div>

        {/* Analysis Results */}
        {analysis && (
          <div className="space-y-4 mt-4">
            {/* Investor Header */}
            <div className="p-4 bg-slate-50 rounded-lg flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{analysis.investor_data?.name}</h3>
                <p className="text-sm text-slate-600">{analysis.investor_data?.location}</p>
                {analysis.investor_data?.website && (
                  <a href={analysis.investor_data.website} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 flex items-center gap-1 mt-1">
                    <Globe className="w-3 h-3" /> Website
                  </a>
                )}
              </div>
              <div className={`px-4 py-3 rounded-lg ${analysis.overall_score >= 70 ? 'bg-green-100' : analysis.overall_score >= 50 ? 'bg-amber-100' : 'bg-red-100'}`}>
                <div className={`text-3xl font-bold ${analysis.overall_score >= 70 ? 'text-green-600' : analysis.overall_score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                  {analysis.overall_score}%
                </div>
                <div className="text-xs text-slate-600">Match Score</div>
              </div>
            </div>

            {/* Conflict Warning */}
            {analysis.flags?.some(f => f.type === "competitor_warning") && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-900">Competitor Alert</h4>
                    <p className="text-sm text-red-700 mt-1">
                      {analysis.flags.find(f => f.type === "competitor_warning")?.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Score Breakdown */}
            <div className="space-y-3">
              <h4 className="font-medium text-slate-900">Investor Analysis</h4>
              
              {/* Thesis Alignment */}
              <div className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Thesis Alignment</span>
                  <Badge className={getScoreColorClass(analysis.match_analysis?.thesis?.score, analysis.match_analysis?.thesis?.max)}>
                    {analysis.match_analysis?.thesis?.score}/{analysis.match_analysis?.thesis?.max}
                  </Badge>
                </div>
                <Progress value={(analysis.match_analysis?.thesis?.score / analysis.match_analysis?.thesis?.max) * 100} className="h-2 mb-2" />
                <ul className="text-xs text-slate-600 space-y-1">
                  {analysis.match_analysis?.thesis?.notes?.map((note, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <CheckCircle className="w-3 h-3 mt-0.5 text-slate-400 flex-shrink-0" />
                      {note}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Stage Alignment */}
              <div className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Stage Alignment</span>
                  <Badge className={getScoreColorClass(analysis.match_analysis?.stage?.score, analysis.match_analysis?.stage?.max)}>
                    {analysis.match_analysis?.stage?.score}/{analysis.match_analysis?.stage?.max}
                  </Badge>
                </div>
                <Progress value={(analysis.match_analysis?.stage?.score / analysis.match_analysis?.stage?.max) * 100} className="h-2 mb-2" />
                <ul className="text-xs text-slate-600 space-y-1">
                  {analysis.match_analysis?.stage?.notes?.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              </div>

              {/* Portfolio Analysis */}
              <div className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Portfolio Fit</span>
                  <Badge className={getScoreColorClass(Math.max(0, analysis.match_analysis?.portfolio?.score), analysis.match_analysis?.portfolio?.max)}>
                    {analysis.match_analysis?.portfolio?.score}/{analysis.match_analysis?.portfolio?.max}
                  </Badge>
                </div>
                <ul className="text-xs text-slate-600 space-y-1">
                  {analysis.match_analysis?.portfolio?.notes?.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
                {analysis.match_analysis?.portfolio?.opportunities?.length > 0 && (
                  <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                    <span className="font-medium text-green-700">Opportunity: </span>
                    <span className="text-green-600">{analysis.match_analysis?.portfolio?.opportunities[0]?.company} - {analysis.match_analysis?.portfolio?.opportunities[0]?.opportunity}</span>
                  </div>
                )}
              </div>

              {/* Activity */}
              <div className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Investment Activity</span>
                  <Badge className={getScoreColorClass(analysis.match_analysis?.activity?.score, analysis.match_analysis?.activity?.max)}>
                    {analysis.match_analysis?.activity?.score}/{analysis.match_analysis?.activity?.max}
                  </Badge>
                </div>
                <ul className="text-xs text-slate-600 space-y-1">
                  {analysis.match_analysis?.activity?.notes?.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Recommendations */}
            {analysis.recommendations?.length > 0 && (
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <h4 className="font-medium text-teal-900 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Recommendations
                </h4>
                <ul className="space-y-1">
                  {analysis.recommendations.map((rec, i) => (
                    <li key={i} className="text-sm text-teal-800 flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-2 border-t">
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save to Database
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InvestorsContent({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [matchedInvestors, setMatchedInvestors] = useState([]);
  const [totalMatched, setTotalMatched] = useState(0);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showPrepare, setShowPrepare] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [prepareData, setPrepareData] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [showAnalyze, setShowAnalyze] = useState(false);
  
  // Paywall state
  const [hasFullAccess, setHasFullAccess] = useState(true);
  const [paywalled, setPaywalled] = useState(false);
  const [paywalledCount, setPaywalledCount] = useState(0);
  const [showPaywall, setShowPaywall] = useState(false);
  
  // Contribution modal state
  const [showContribute, setShowContribute] = useState(false);
  const [contributeEntity, setContributeEntity] = useState(null);
  
  // Pipeline state
  const [addingToPipeline, setAddingToPipeline] = useState(false);
  
  // User network prompt state
  const [showInvestorsPrompt, setShowInvestorsPrompt] = useState(false);
  const [hasUserInvestors, setHasUserInvestors] = useState(true); // Assume true until checked
  
  // Fundraise settings
  const [raiseAmount, setRaiseAmount] = useState("");
  const [estimatedValuation, setEstimatedValuation] = useState("");
  const [nextFundraiseStage, setNextFundraiseStage] = useState("");
  const [fundraiseTimeframe, setFundraiseTimeframe] = useState("");
  const [excludeEisSeis, setExcludeEisSeis] = useState(false);

  useEffect(() => {
    fetchMatchedInvestors();
    checkUserNetwork();
  }, []);
  
  // Check if user has added their investors
  const checkUserNetwork = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/network`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setHasUserInvestors(data.has_investors);
        // Show prompt if no investors and hasn't dismissed before
        const dismissed = localStorage.getItem("investorsPromptDismissed");
        if (!data.has_investors && !dismissed) {
          setShowInvestorsPrompt(true);
        }
      }
    } catch (e) {
      console.error("Failed to check user network:", e);
    }
  };
  
  const dismissInvestorsPrompt = () => {
    setShowInvestorsPrompt(false);
    localStorage.setItem("investorsPromptDismissed", "true");
  };
  
  // Refetch when fundraise settings change
  useEffect(() => {
    if (!loading) {
      fetchMatchedInvestors();
    }
  }, [nextFundraiseStage, fundraiseTimeframe, excludeEisSeis]);

  const fetchMatchedInvestors = async () => {
    try {
      const params = new URLSearchParams();
      if (nextFundraiseStage) params.append("next_stage", nextFundraiseStage);
      if (raiseAmount) params.append("raise_amount", raiseAmount);
      if (fundraiseTimeframe) params.append("timeframe", fundraiseTimeframe);
      if (excludeEisSeis) params.append("exclude_eis_seis", "true");
      
      const response = await fetch(`${BACKEND_URL}/api/investors/matched?${params.toString()}`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setMatchedInvestors(data.investors || []);
        setTotalMatched(data.total_matched || 0);
        // Paywall state from API
        setHasFullAccess(data.has_full_access ?? true);
        setPaywalled(data.paywalled ?? false);
        setPaywalledCount(data.paywalled_count ?? 0);
      }
      
      const profileRes = await fetch(`${BACKEND_URL}/api/company-profile`, {
        credentials: "include"
      });
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setCompanyProfile(profileData.profile || profileData);
        // Set default next stage based on current funding stage
        if (profileData.profile?.funding_stage && !nextFundraiseStage) {
          const stageProgression = {
            "pre-seed": "seed",
            "seed": "series-a",
            "series-a": "series-b",
            "series-b": "series-c",
            "series-c": "series-c"
          };
          setNextFundraiseStage(stageProgression[profileData.profile.funding_stage] || "seed");
        }
      }
    } catch (error) {
      toast.error("Failed to load investor matches");
    } finally {
      setLoading(false);
    }
  };

  const handlePrepareForInvestors = async () => {
    setPreparing(true);
    setShowPrepare(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/investors/prepare`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrepareData(data);
      }
    } catch (error) {
      toast.error("Failed to generate analysis");
    } finally {
      setPreparing(false);
    }
  };

  const handleAddToPipeline = async (investor) => {
    setAddingToPipeline(true);
    try {
      // Get primary contact from contacts array
      const contacts = investor.contacts || [];
      const primaryContact = contacts[0] || {};
      
      const response = await fetch(`${BACKEND_URL}/api/pipeline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          investor_id: investor.firm_id,
          firm_name: investor.name,
          firm_website: investor.website,
          primary_contact_name: primaryContact.name || null,
          primary_contact_email: primaryContact.email || null,
          primary_contact_role: primaryContact.role || null,
          match_score: investor.match_score,
          match_rationale: investor.score_breakdown?.rationale || null
        })
      });
      
      if (response.ok) {
        toast.success(`${investor.name} added to pipeline!`, {
          action: {
            label: "View Pipeline",
            onClick: () => navigate("/pipeline")
          }
        });
      } else {
        const err = await response.json();
        if (err.detail?.includes("already in pipeline")) {
          toast.info("Already in your pipeline");
        } else {
          toast.error(err.detail || "Failed to add to pipeline");
        }
      }
    } catch (error) {
      toast.error("Failed to add to pipeline");
    } finally {
      setAddingToPipeline(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="page-title">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            Investors
          </h1>
          <p className="text-slate-600 mt-1">Top 10 investors for you</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAnalyze(true)} variant="outline" className="gap-2" data-testid="btn-analyse-investor">
            <Search className="w-4 h-4" />
            Analyse investor
          </Button>
        </div>
      </div>
      
      {/* Fundraise Settings */}
      <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-4 h-4 text-teal-600" />
          <span className="font-medium text-slate-900">Your next funding round</span>
          <span className="text-xs text-slate-500">(used for investor matching)</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
          <div>
            <Label className="text-xs text-slate-600">Target Round</Label>
            <select
              value={nextFundraiseStage}
              onChange={(e) => setNextFundraiseStage(e.target.value)}
              className="w-full mt-1 h-9 px-3 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="select-target-round"
            >
              <option value="pre-seed">Pre-Seed</option>
              <option value="seed">Seed</option>
              <option value="series-a">Series A</option>
              <option value="series-b">Series B</option>
              <option value="series-c">Series C+</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Raise Amount</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
              <Input
                type="text"
                placeholder="e.g. 5M"
                value={raiseAmount}
                onChange={(e) => setRaiseAmount(e.target.value)}
                className="pl-7 h-9"
                data-testid="input-raise-amount"
              />
            </div>
          </div>
          <Button
            onClick={fetchMatchedInvestors}
            disabled={loading}
            className="h-9 gap-2 bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="btn-update-matches"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Update matches
          </Button>
        </div>
        
        {/* EIS/SEIS Preference */}
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <Checkbox 
              id="exclude-eis-seis"
              checked={excludeEisSeis}
              onCheckedChange={(checked) => setExcludeEisSeis(checked)}
            />
            <div>
              <Label htmlFor="exclude-eis-seis" className="text-sm font-medium cursor-pointer">
                Exclude EIS/SEIS-only investors
              </Label>
              <p className="text-xs text-slate-500">
                Some investors only deploy via EIS/SEIS schemes. Check this to filter them out.
              </p>
            </div>
          </div>
        </div>
      </div>

      {matchedInvestors.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Investors Yet</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Complete your company profile to get investor recommendations.
            </p>
            <Button onClick={() => navigate("/profile")} className="gap-2">
              Complete Profile <ChevronRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-teal-600" />
              <span className="text-teal-800">
                {hasFullAccess ? (
                  <><strong>Top 10</strong> investors shown</>
                ) : (
                  <><strong>Top 3</strong> shown • {paywalledCount}+ more available</>
                )}
              </span>
            </div>
            <Badge className="bg-teal-600">Personalised</Badge>
          </div>

          {(() => {
            const leads = matchedInvestors.filter((i) => i.leads_rounds);
            const followers = matchedInvestors.filter((i) => !i.leads_rounds);

            const renderBlock = (title, subtitle, Icon, list, iconColor, dataTestId) => (
              <div className="mb-8" data-testid={dataTestId}>
                <div className="flex items-baseline gap-3 mb-4 pb-2 border-b border-slate-200">
                  <Icon className={`w-5 h-5 ${iconColor}`} />
                  <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
                  <span className="text-xs text-slate-500">{subtitle}</span>
                  <span className="ml-auto text-xs font-mono text-slate-400 uppercase tracking-widest">
                    {list.length} {list.length === 1 ? "firm" : "firms"}
                  </span>
                </div>
                {list.length === 0 ? (
                  <div className="text-sm text-slate-500 italic px-1 py-6 text-center border border-dashed border-slate-200 rounded-lg">
                    No {title.toLowerCase()} identified in the current match set.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {list.map((investor) => (
                      <MatchedInvestorCard
                        key={investor.firm_id}
                        investor={investor}
                        onClick={() => { setSelectedInvestor(investor); setShowDetail(true); }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );

            return (
              <>
                {renderBlock(
                  "Potential leads",
                  "Firms that typically lead rounds at your stage",
                  Crown,
                  leads,
                  "text-amber-500",
                  "potential-leads-block"
                )}
                {renderBlock(
                  "Potential followers",
                  "Firms that typically follow when a lead is in place",
                  Users2,
                  followers,
                  "text-teal-500",
                  "potential-followers-block"
                )}
              </>
            );
          })()}

          {/* Paywall Banner for non-subscribers */}
          {paywalled && (
            <div className="mt-6 p-6 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl text-white text-center">
              <Lock className="w-8 h-8 mx-auto mb-3 text-amber-400" />
              <h3 className="text-xl font-bold mb-2">
                {paywalledCount}+ More Investors Waiting
              </h3>
              <p className="text-slate-300 mb-4 max-w-md mx-auto">
                Unlock full access to all investors, detailed insights, and personalised recommendations.
              </p>
              <Button 
                onClick={() => setShowPaywall(true)}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                Unlock Full Access
              </Button>
            </div>
          )}
        </>
      )}
      
      <InvestorDetailDialog
        investor={selectedInvestor}
        open={showDetail}
        onClose={() => setShowDetail(false)}
        companyProfile={companyProfile}
        onAddToPipeline={handleAddToPipeline}
        addingToPipeline={addingToPipeline}
      />
      
      <PrepareDialog
        open={showPrepare}
        onClose={() => setShowPrepare(false)}
        data={prepareData}
        loading={preparing}
      />
      
      <AnalyzeInvestorDialog
        open={showAnalyze}
        onClose={() => setShowAnalyze(false)}
        onAnalysisComplete={() => { fetchMatchedInvestors(); setShowAnalyze(false); }}
      />
      
      {/* Paywall Modal */}
      <PaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        paywallCount={paywalledCount}
        category="investors"
        onSubscribe={() => setShowPaywall(false)}
        onPromoApplied={() => { setShowPaywall(false); fetchMatchedInvestors(); }}
      />
      
      {/* Contribution Modal */}
      <ContributionModal
        open={showContribute}
        onClose={() => { setShowContribute(false); setContributeEntity(null); }}
        category="investor"
        entityId={contributeEntity?.firm_id}
        entityName={contributeEntity?.name}
        onSuccess={() => toast.success("Thank you for your contribution!")}
      />
      
      {/* Add Investors Prompt Modal */}
      <Dialog open={showInvestorsPrompt} onOpenChange={setShowInvestorsPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-600" />
              Unlock Better Intro Paths
            </DialogTitle>
            <DialogDescription>
              Tell us which investors you've worked with before. This helps us find warm introduction paths to new investors.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
              <p className="text-sm text-teal-800">
                By adding your previous investors, we can identify connections between your network and potential new investors, making introductions smoother.
              </p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => { setShowInvestorsPrompt(false); navigate("/profile"); }} className="flex-1">
                <Users className="w-4 h-4 mr-2" />
                Add My Investors
              </Button>
              <Button variant="outline" onClick={dismissInvestorsPrompt}>
                Maybe Later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Investors() {
  return (
    <AppLayout>
      {(user) => <InvestorsContent user={user} />}
    </AppLayout>
  );
}
